import React from 'react';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from '@/components/ui/command';
import { cn } from '@/lib/utils';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
} from '@/components/ui/select';
import { Community } from '@/types';

const ALL_COMMUNITIES_VALUE = '__all_communities__';

interface CommunityScopeSelectorProps {
  communities: Community[];
  value: string[];
  onChange?: (communityIds: string[]) => void;
  label?: string;
  placeholder?: string;
  className?: string;
  triggerClassName?: string;
  disabled?: boolean;
  singleSelect?: boolean;
  allowAllOption?: boolean;
  allOptionLabel?: string;
}

export const CommunityScopeSelector: React.FC<CommunityScopeSelectorProps> = ({
  communities,
  value,
  onChange,
  label = 'Comunidad',
  placeholder = 'Selecciona una o varias comunidades',
  className,
  triggerClassName,
  disabled = false,
  singleSelect = false,
  allowAllOption = false,
  allOptionLabel = 'Todas las comunidades',
}) => {
  const hasCommunities = communities.length > 0;
  const selectDisabled = disabled || !hasCommunities || !onChange;

  const singleSelectedCommunityName = React.useMemo(() => {
    if (!singleSelect) {
      return null;
    }

    const selectedId = value[0];
    if (!selectedId) {
      return hasCommunities && allowAllOption ? allOptionLabel : hasCommunities ? placeholder : 'Sin comunidades disponibles';
    }

    const selected = communities.find((community) => community.id === selectedId);
    return selected?.name ?? (hasCommunities ? placeholder : 'Sin comunidades disponibles');
  }, [allOptionLabel, allowAllOption, communities, hasCommunities, placeholder, singleSelect, value]);

  const handleSingleSelectChange = React.useCallback((communityId: string) => {
    if (!onChange) return;
    if (communityId === ALL_COMMUNITIES_VALUE) {
      onChange([]);
      return;
    }
    onChange([communityId]);
  }, [onChange]);

  const handleToggleCommunity = React.useCallback((communityId: string) => {
    if (!onChange) {
      return;
    }

    const next = new Set(value);
    if (next.has(communityId)) {
      next.delete(communityId);
    } else {
      next.add(communityId);
    }
    const orderedSelection = communities
      .filter(community => next.has(community.id))
      .map(community => community.id);

    onChange(orderedSelection);
  }, [communities, onChange, value]);

  const containerLayoutClass = singleSelect
    ? 'flex w-full flex-col gap-2 md:flex-row md:items-center md:gap-3'
    : 'flex w-full flex-col gap-2 md:ml-auto md:w-auto md:flex-row md:items-center md:justify-end md:gap-3';

  return (
    <div
      className={cn(
        containerLayoutClass,
        className,
      )}
    >
      {label ? (
        <Label className="text-sm font-medium text-muted-foreground md:mb-0 md:text-left">
          {label}
        </Label>
      ) : null}
      <div className={cn("flex flex-1 min-w-0 flex-col gap-2", singleSelect ? "w-full md:min-w-[200px]" : "md:flex-none md:min-w-[220px]")}>
        {singleSelect ? (
          <Select
            value={value[0] ?? (allowAllOption ? ALL_COMMUNITIES_VALUE : undefined)}
            onValueChange={handleSingleSelectChange}
            disabled={selectDisabled}
          >
            <SelectTrigger className={cn('w-full md:w-[240px]', triggerClassName)}>
              <span className="block truncate text-left">{singleSelectedCommunityName}</span>
            </SelectTrigger>
            <SelectContent>
              {allowAllOption ? (
                <SelectItem value={ALL_COMMUNITIES_VALUE}>
                  {allOptionLabel}
                </SelectItem>
              ) : null}
              {communities.map((community) => (
                <SelectItem key={community.id} value={community.id}>
                  {community.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        ) : (
          <Command className="rounded-md border">
            <div className="border-b px-3 py-2">
              <CommandInput placeholder="Buscar comunidad..." className="h-9" />
            </div>
            <CommandList className="max-h-64">
              <CommandEmpty>Sin resultados</CommandEmpty>
              <CommandGroup>
                {communities.map(community => {
                  const isSelected = value.includes(community.id);
                  return (
                    <CommandItem
                      key={community.id}
                      value={community.name}
                      onSelect={() => handleToggleCommunity(community.id)}
                      className="flex cursor-pointer select-none items-center justify-between gap-2 px-3"
                    >
                      <div className="flex flex-col text-left">
                        <span className="text-sm font-medium text-foreground">{community.name}</span>
                        <span className="text-xs text-muted-foreground">{community.city}</span>
                      </div>
                      {isSelected ? <Badge variant="secondary">Seleccionada</Badge> : null}
                    </CommandItem>
                  );
                })}
              </CommandGroup>
            </CommandList>
          </Command>
        )}
      </div>
    </div>
  );
};
